import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CalendarRange, Bus, Users, Sparkles, Tent } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1674230316788-d9c8b92f0d63?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };
const dayLabel = (iso) => new Date(iso + "T12:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" });

export default function CampsPublic() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ camps: [] });

  useEffect(() => {
    document.title = "Camps — Adventure Rock Bermuda";
    api.get("/camps").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const rate = data.founding_active ? (data.founding_daily || data.daily_rate) : data.daily_rate;
  const goRegister = (id) => nav(user ? `/dashboard/camps?camp=${id}` : "/login");

  return (
    <div className="bg-navy min-h-screen text-white overflow-x-hidden">
      <Navbar />

      <section className="relative">
        <div className="absolute inset-0"><img src={HERO} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" /><div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/80 to-navy" /></div>
        <div className="relative max-w-5xl mx-auto px-4 pt-28 pb-14 text-center">
          <motion.p {...fade} className="text-gold font-bold tracking-[0.3em] text-xs">SCHOOL BREAK ADVENTURES</motion.p>
          <motion.h1 {...fade} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-2">Adventure Rock <span className="text-gold">Camps</span></motion.h1>
          <motion.p {...fade} className="text-white/75 mt-4 max-w-2xl mx-auto">Full-day holiday camps packed with inflatables, games, arts & crafts and outdoor fun. Flexible 3-day and 5-day passes — pick the days that suit your family.</motion.p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 pb-16 -mt-4 space-y-8">
        {data.camps.length === 0 && <p className="text-center text-white/50" data-testid="camps-empty">No camps are scheduled right now — check back soon!</p>}
        {data.camps.map((c) => {
          const full = c.spots_left != null && c.spots_left <= 0;
          const closed = c.status === "closed";
          return (
            <motion.div key={c.id} {...fade} className="rounded-3xl bg-gradient-to-br from-ocean to-navy border border-turquoise/20 p-6 sm:p-8" data-testid={`camp-card-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display font-extrabold text-3xl flex items-center gap-2"><span>{c.emoji}</span> {c.name}</h2>
                  <p className="text-turquoise font-semibold mt-1">{c.tagline}</p>
                </div>
                {c.spots_left != null && !closed && (
                  <span className={`text-xs font-bold rounded-full px-3 py-1 ${full ? "bg-coral/20 text-coral" : "bg-turquoise/15 text-turquoise"}`} data-testid={`camp-spots-${c.id}`}>
                    <Users className="w-3 h-3 inline mr-1" />{full ? "Fully booked" : `${c.spots_left} spots left`}
                  </span>
                )}
              </div>

              <p className="text-white/70 mt-4 leading-relaxed">{c.description}</p>

              <div className="grid sm:grid-cols-3 gap-3 mt-6">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><CalendarRange className="w-5 h-5 text-gold mb-2" /><p className="text-xs text-white/50">Camp dates</p><p className="font-semibold text-sm">{dayLabel(c.dates[0])} – {dayLabel(c.dates[c.dates.length - 1])}</p></div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><Tent className="w-5 h-5 text-gold mb-2" /><p className="text-xs text-white/50">Options</p><p className="font-semibold text-sm">{c.day_options.map((n) => `${n}-day`).join(" · ")}</p></div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><Sparkles className="w-5 h-5 text-gold mb-2" /><p className="text-xs text-white/50">From</p><p className="font-display font-black text-2xl text-gold">${(c.day_price || rate || 0) * Math.min(...c.day_options)}<span className="text-xs text-white/50 font-normal"> / {Math.min(...c.day_options)} days</span></p></div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                {c.dates.map((d) => <span key={d} className="text-xs bg-white/10 rounded-lg px-2.5 py-1.5 font-semibold">{dayLabel(d)}</span>)}
              </div>

              <div className="flex items-center gap-3 mt-6 flex-wrap">
                <Button disabled={full || closed} onClick={() => goRegister(c.id)} className="bg-gold hover:bg-gold/90 text-navy font-black rounded-full px-8 py-6 text-base disabled:opacity-50" data-testid={`camp-register-${c.id}`}>
                  {closed ? "Registration closed" : full ? "Fully booked" : "Register for Camp"}
                </Button>
                <p className="text-white/50 text-xs flex items-center gap-1"><Bus className="w-4 h-4" /> {c.transport_enabled ? "Mini-bus transport available" : "Drop-off only"} · pay with cash or WOI</p>
              </div>
            </motion.div>
          );
        })}
      </section>

      <Footer />
    </div>
  );
}
